import React from 'react';
import { useTranslation } from 'react-i18next';

import { CustomerHeader, ShipTo } from '../../services/types';

interface CustomerHeaderCardProps {
  header: CustomerHeader;
  shipTos: ShipTo[];
  selectedShipTo: ShipTo | null;
  onShipToChange: (shipToNum: string) => void;
}

function joinAddress(parts: (string | undefined)[]): string {
  return parts.filter((p) => p && p.trim().length > 0).join(', ');
}

/**
 * Customer dashboard header, styled to match the Sales Order Tracker:
 * a blue header band with a ship-to selector, and three colored info cards
 * (Address, Billing, General Info).
 */
export const CustomerHeaderCard: React.FC<CustomerHeaderCardProps> = ({
  header,
  shipTos,
  selectedShipTo,
  onShipToChange,
}) => {
  const { t } = useTranslation();

  // When a ship-to is selected, its address / incoterms / transport take
  // precedence; otherwise fall back to the customer (sold-to) values.
  const streetLine = selectedShipTo
    ? joinAddress([
        selectedShipTo.address1,
        selectedShipTo.address2,
        selectedShipTo.address3,
      ])
    : joinAddress([header.address1, header.address2, header.address3]);

  const cityLine = selectedShipTo
    ? joinAddress([
        selectedShipTo.city,
        selectedShipTo.state,
        selectedShipTo.zip,
        selectedShipTo.country,
      ])
    : joinAddress([header.city, header.state, header.zip, header.country]);

  const incotermCode = selectedShipTo
    ? selectedShipTo.incotermCode
    : header.incotermCode;
  const incotermDesc = selectedShipTo
    ? selectedShipTo.incotermsDescription
    : header.incotermsDescription;
  const incoterms = incotermCode
    ? `${incotermCode}${incotermDesc ? ` — ${incotermDesc}` : ''}`
    : t('customerDashboard.header.noIncoterms');

  const shipVia = selectedShipTo
    ? selectedShipTo.shipViaDescription || selectedShipTo.shipViaCode
    : header.shipViaDescription || header.shipViaCode;

  const paymentTerms = header.termsDescription || header.termsCode || '—';
  const currency = header.currencyCode || '—';

  return (
    <div className="bg-white rounded border border-gray-300 shadow-sm">
      {/* Header band */}
      <div className="bg-blue-50 px-4 py-3 border-b border-blue-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-blue-900">{header.name}</h2>
          <p className="text-xs text-blue-700">
            {t('customerDashboard.header.customerNumber')}: {header.custId}
          </p>
        </div>

        {/* Ship-to selector */}
        {shipTos.length > 0 && (
          <div className="flex items-center gap-2">
            <label
              htmlFor="shipToSelect"
              className="text-xs font-semibold text-blue-900 uppercase tracking-tight"
            >
              {t('customerDashboard.header.shipTo')}
            </label>
            <select
              id="shipToSelect"
              value={selectedShipTo?.shipToNum ?? ''}
              onChange={(e) => onShipToChange(e.target.value)}
              className="min-w-[220px] px-3 py-1.5 rounded-md text-sm text-gray-900 bg-white border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">
                {t('customerDashboard.header.allShipTos')}
              </option>
              {shipTos.map((shipTo) => (
                <option key={shipTo.shipToNum} value={shipTo.shipToNum}>
                  {shipTo.shipToNum} — {shipTo.name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Three colored info cards */}
      <div className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Address (green) */}
          <div className="bg-green-50 rounded-lg p-4 border border-green-200">
            <h3 className="text-sm font-bold text-green-900 uppercase tracking-tight mb-3">
              {selectedShipTo
                ? t('customerDashboard.header.shipToAddress')
                : t('customerDashboard.header.address')}
            </h3>
            <div className="space-y-2">
              <p className="text-sm font-semibold text-gray-900">
                {selectedShipTo ? selectedShipTo.name : header.name}
                {selectedShipTo && (
                  <span className="text-gray-600 font-normal">
                    {' '}
                    ({selectedShipTo.shipToNum})
                  </span>
                )}
              </p>
              <p className="text-xs text-gray-700">{streetLine || '—'}</p>
              <p className="text-xs text-gray-700">{cityLine || '—'}</p>
              {selectedShipTo?.contactName && (
                <p className="text-xs text-gray-700">
                  <span className="font-medium">
                    {t('customerDashboard.header.contact')}:
                  </span>{' '}
                  {selectedShipTo.contactName}
                </p>
              )}
            </div>
          </div>

          {/* Billing (purple) */}
          <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
            <h3 className="text-sm font-bold text-purple-900 uppercase tracking-tight mb-3">
              {t('customerDashboard.header.billing')}
            </h3>
            <div className="grid grid-cols-1 gap-y-2 text-xs">
              <div>
                <span className="text-gray-600">
                  {t('customerDashboard.header.paymentTerms')}:
                </span>
                <span className="ml-1 font-medium text-gray-900">
                  {paymentTerms}
                </span>
              </div>
              <div>
                <span className="text-gray-600">
                  {t('customerDashboard.header.currency')}:
                </span>
                <span className="ml-1 font-medium text-gray-900">
                  {currency}
                </span>
              </div>
            </div>
          </div>

          {/* General Info (gray) */}
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <h3 className="text-sm font-bold text-gray-700 uppercase tracking-tight mb-3">
              {t('customerDashboard.header.generalInfo')}
            </h3>
            <div className="grid grid-cols-1 gap-y-2 text-xs">
              <div>
                <span className="text-gray-600">
                  {t('customerDashboard.header.transport')}:
                </span>
                <span className="ml-1 font-medium text-gray-900">
                  {shipVia || '—'}
                </span>
              </div>
              <div>
                <span className="text-gray-600">
                  {t('customerDashboard.header.incoterms')}:
                </span>
                <span className="ml-1 font-medium text-gray-900">
                  {incoterms}
                </span>
              </div>
              <div>
                <span className="text-gray-600">
                  {t('customerDashboard.header.customerNumber')}:
                </span>
                <span className="ml-1 font-medium text-gray-900">
                  {header.custId}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
